import { IVirtualBackgroundConfig, IDeviceCapabilities, TVBPreset } from '@src/types';

/**
 * Preset configurations for different use cases
 * Simplified to only include user-controllable parameters
 */
export const VB_PRESETS: Record<Exclude<TVBPreset, 'custom'>, Partial<IVirtualBackgroundConfig>> = {
  lite: {
    // Ultra-low settings for very old/weak devices
    preset: 'lite',
    inferenceScale: 0.35,
    segmentationFPS: 8,
    delegate: 'GPU',
    smoothing: 0.6, // Higher smoothing to compensate for low FPS
    edgeBlur: 4,
  },
  performance: {
    // Optimized for low CPU usage and battery saving
    preset: 'performance',
    inferenceScale: 0.5,
    segmentationFPS: 12,
    delegate: 'GPU',
    smoothing: 0.45,
    edgeBlur: 3,
  },
  balanced: {
    // Default balanced settings - good quality with reasonable performance
    preset: 'balanced',
    inferenceScale: 0.65,
    segmentationFPS: 20,
    delegate: 'GPU',
    smoothing: 0.25,
    edgeBlur: 2,
  },
  quality: {
    // Optimized for best visual quality on high-end devices
    preset: 'quality',
    inferenceScale: 0.85,
    segmentationFPS: 24,
    delegate: 'GPU',
    smoothing: 0.1,
    edgeBlur: 1,
  },
  responsive: {
    // Optimized for fast motion - minimal ghosting
    preset: 'responsive',
    inferenceScale: 0.6,
    segmentationFPS: 24,
    delegate: 'GPU',
    smoothing: 0.1, // Very low smoothing for responsive tracking
    edgeBlur: 2,
  },
  rvm: {
    // Optimized for Robust Video Matting AI engine
    // RVM uses ONNX WASM which is slower than MediaPipe's TFLite
    preset: 'rvm',
    inferenceScale: 0.2, // Ultra-low scale for single-threaded performance
    segmentationFPS: 10, // Very low FPS targets for single-threaded
    delegate: 'GPU',
    smoothing: 0.1, // Reduced smoothing
    edgeBlur: 2,
    segmentationBackend: 'rvm',
  },
};

/**
 * Apply a preset configuration
 * @param preset - The preset to apply
 * @returns The configuration object
 */
export const applyPreset = (preset: TVBPreset): Partial<IVirtualBackgroundConfig> => {
  if (preset === 'custom') {
    return { preset: 'custom' };
  }
  return { ...VB_PRESETS[preset], preset };
};

/**
 * Get Auto-Performance Configuration
 *
 * Returns optimal configuration based on detected device capabilities.
 * Uses benchmark-derived values for accurate performance tuning.
 *
 * @param {IDeviceCapabilities} capabilities - Detected device capabilities
 * @returns {Partial<IVirtualBackgroundConfig>} Optimal configuration
 */
export const getAutoPerformanceConfig = (capabilities: IDeviceCapabilities): Partial<IVirtualBackgroundConfig> => {
  const { hasGPU, performanceTier, recommendedFPS, recommendedScale } = capabilities;

  // Use benchmark-derived values directly for accurate tuning
  let config: Partial<IVirtualBackgroundConfig>;

  switch (performanceTier) {
    case 'high':
      config = {
        ...VB_PRESETS.quality,
        inferenceScale: recommendedScale,
        segmentationFPS: recommendedFPS,
      };
      break;

    case 'medium':
      config = {
        ...VB_PRESETS.performance,
        inferenceScale: recommendedScale,
        segmentationFPS: recommendedFPS,
      };
      break;

    case 'low':
    default:
      config = {
        ...VB_PRESETS.lite,
        inferenceScale: recommendedScale,
        segmentationFPS: recommendedFPS,
      };
      break;
  }

  // GPU-specific adjustments
  config.delegate = hasGPU ? 'GPU' : 'CPU';
  if (!hasGPU) {
    // CPU-only: further reduce to maintain framerate
    config.inferenceScale = Math.min(config.inferenceScale || 0.5, 0.4);
    config.segmentationFPS = Math.min(config.segmentationFPS || 15, 10);
  }

  return config;
};

/**
 * Get Recommended Preset
 *
 * Returns an appropriate preset based on device capabilities.
 * Used for auto-configuring virtual background on first use.
 *
 * @param {boolean} isMobile - Whether the device is mobile
 * @returns {TVBPreset} Recommended preset name
 */
export const getRecommendedPreset = (isMobile: boolean): TVBPreset => {
  if (isMobile) {
    return 'performance';
  }
  return 'balanced';
};

/**
 * Get recommended settings based on device capabilities
 * @deprecated Use getAutoPerformanceConfig with detectDeviceCapabilities instead
 */
export const getRecommendedSettings = (
  isMobile: boolean = false,
  hasGPU: boolean = true
): Partial<IVirtualBackgroundConfig> => {
  if (isMobile) {
    return {
      ...VB_PRESETS.performance,
      segmentationFPS: 15,
      inferenceScale: 0.5,
      delegate: hasGPU ? 'GPU' : 'CPU',
    };
  }

  if (!hasGPU) {
    return {
      ...VB_PRESETS.performance,
      delegate: 'CPU',
      segmentationFPS: 12,
    };
  }

  return VB_PRESETS.balanced;
};

/**
 * Validate Configuration
 *
 * Ensures all configuration values are within valid ranges.
 * Clamps values to acceptable limits to prevent worker errors.
 *
 * @param {Partial<IVirtualBackgroundConfig>} config - Configuration to validate
 * @returns {Partial<IVirtualBackgroundConfig>} Validated configuration with clamped values
 */
export const validateConfig = (config: Partial<IVirtualBackgroundConfig>): Partial<IVirtualBackgroundConfig> => {
  const validated = { ...config };

  // Clamp values to valid ranges
  if (validated.inferenceScale !== undefined) {
    validated.inferenceScale = Math.max(0.2, Math.min(1.0, validated.inferenceScale));
  }

  if (validated.segmentationFPS !== undefined) {
    validated.segmentationFPS = Math.max(5, Math.min(30, Math.round(validated.segmentationFPS)));
  }

  if (validated.smoothing !== undefined) {
    validated.smoothing = Math.max(0, Math.min(1, validated.smoothing));
  }

  if (validated.edgeBlur !== undefined) {
    validated.edgeBlur = Math.max(0, Math.min(20, Math.round(validated.edgeBlur)));
  }

  return validated;
};
